import { useState, useMemo, useCallback, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Building2,
  Filter,
  Loader2,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Search,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisLabel, KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import {
  useSupplierInsights,
  useChartFilterOptions,
} from "@/hooks/useInvoices";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";
import { useSnapshotPrint } from "@/context/SnapshotPrintContext";
import { sliceForSnapshotPrint } from "@/lib/snapshotPrint";
import { useQueryClient } from "@tanstack/react-query";
import { canManageAgencyUsers } from "@/lib/roles";

const AIVIA_COLORS = [
  "#ff7540", // Coral (Primary)
  "#9e3e19", // Burnt Orange (Primary Accent)
  "#3b82f6", // Blue
  "#10b981", // Emerald
  "#84cc16", // Lime
  "#f59e0b", // Orange
  "#ec4899", // Pink
  "#ef4444", // Red
  "#8b5cf6", // Violet
  "#06b6d4", // Cyan
];

const PIE_TOP_N = 5;
const PAGE_SIZE = 5;

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

interface SupplierInsightsCardProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  userRole?: string | null;
  returnDateFrom?: string;
  returnDateTo?: string;
}

type SortField = "supplier" | "invoiceCount" | "totalSales" | "totalCommission";
type SortDir = "asc" | "desc";

function AdvisorFilter({
  options,
  selected,
  onChange,
}: {
  options: Array<{ value: string; label: string }>;
  selected: number[];
  onChange: (ids: number[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = useCallback(
    (value: string) => {
      const id = Number(value);
      const next = selected.includes(id)
        ? selected.filter((v) => v !== id)
        : [...selected, id];
      onChange(next);
    },
    [selected, onChange],
  );

  const hasSelection = selected.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`relative rounded-md p-1.5 transition-colors hover:bg-muted max-md:inline-flex max-md:min-h-11 max-md:min-w-11 max-md:items-center max-md:justify-center ${
            hasSelection ? "text-orange-600" : "text-muted-foreground"
          }`}
          aria-label="Filter by advisor"
        >
          <Filter className="h-4 w-4" />
          {hasSelection && (
            <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-500 text-[9px] font-bold text-white">
              {selected.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search advisors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-7 text-xs"
            />
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">
              No advisors found
            </p>
          ) : (
            filtered.map((option) => {
              const isSelected = selected.includes(Number(option.value));
              return (
                <button
                  key={option.value}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
                  onClick={() => toggle(option.value)}
                >
                  <div
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      isSelected
                        ? "border-orange-500 bg-orange-500 text-white"
                        : "border-muted-foreground/30"
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })
          )}
        </div>
        {hasSelection && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs h-7"
              onClick={() => {
                onChange([]);
                setSearch("");
              }}
            >
              Clear all
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function PieTooltipContent({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const { supplier, invoiceCount, percent } = entry.payload;
  return (
    <div className="rounded-lg border bg-card px-3 py-2 shadow-md text-xs">
      <p className="font-semibold break-words max-w-[200px]">{supplier}</p>
      <p className="text-muted-foreground mt-0.5">
        {invoiceCount} invoice{invoiceCount !== 1 ? "s" : ""} &middot;{" "}
        {percent.toFixed(1)}%
      </p>
    </div>
  );
}

function SortableHeader({
  label,
  field,
  currentField,
  currentDir,
  onSort,
  align = "center",
  className,
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (field: SortField) => void;
  align?: "left" | "right" | "center";
  /** AR-316: lets callers drop a column below `md`. */
  className?: string;
}) {
  const isActive = currentField === field;
  const ariaSort: "none" | "ascending" | "descending" = isActive
    ? currentDir === "asc"
      ? "ascending"
      : "descending"
    : "none";
  const sortHint = isActive
    ? currentDir === "desc"
      ? "sorted descending"
      : "sorted ascending"
    : "not sorted";
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const btnJustify =
    align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";

  return (
    <th
      aria-sort={ariaSort}
      scope="col"
      className={`px-2 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider md:px-3 ${alignClass} ${className ?? ""}`}
    >
      <button
        type="button"
        className={`group inline-flex min-h-11 w-full items-center gap-0.5 md:min-h-0 ${btnJustify} rounded px-0.5 py-0.5 select-none hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
        onClick={() => onSort(field)}
        aria-label={`Sort by ${label}, ${sortHint}`}
      >
        <span className="inline-flex items-center gap-0.5">
          {label}
          {isActive ? (
            currentDir === "desc" ? (
              <ChevronDown className="h-3 w-3" aria-hidden />
            ) : (
              <ChevronUp className="h-3 w-3" aria-hidden />
            )
          ) : (
            <ChevronsUpDown className="h-3 w-3 opacity-30 group-hover:opacity-50" aria-hidden />
          )}
        </span>
      </button>
    </th>
  );
}

const RADIAN = Math.PI / 180;
function renderPieLabel({
  cx,
  cy,
  midAngle,
  outerRadius,
  supplier,
  percent,
}: any) {
  const radius = outerRadius + 20;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const truncated =
    supplier.length > 14 ? supplier.slice(0, 14) + "…" : supplier;
  return (
    <text
      x={x}
      y={y}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      className="fill-foreground"
      fontSize={11}
    >
      {truncated} {percent.toFixed(0)}%
    </text>
  );
}

export function SupplierInsightsCard({
  tramsAccountId,
  viewMode,
  userRole,
  returnDateFrom,
  returnDateTo,
}: SupplierInsightsCardProps) {
  const queryClient = useQueryClient();
  const isPrinting = useSnapshotPrint();
  const hasCompanyAccess = useMemo(
    () => canManageAgencyUsers(userRole),
    [userRole],
  );

  const [advisorIds, setAdvisorIds] = useState<number[]>([]);
  const [sortField, setSortField] = useState<SortField>("totalCommission");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(0);

  const { data: filterOptions } = useChartFilterOptions({
    tramsAccountId,
    viewMode,
    returnDateFrom,
    returnDateTo,
  });

  const advisorOptions = useMemo(
    () =>
      (filterOptions?.advisors ?? []).map((a) => ({
        value: String(a.id),
        label: a.name,
      })),
    [filterOptions?.advisors],
  );

  const advisorFilterActive = hasCompanyAccess && viewMode === "all";

  const queryParams = useMemo(
    () => ({
      tramsAccountId,
      viewMode,
      advisorIds:
        advisorFilterActive && advisorIds.length > 0 ? advisorIds : undefined,
      returnDateFrom: returnDateFrom || undefined,
      returnDateTo: returnDateTo || undefined,
    }),
    [
      tramsAccountId,
      viewMode,
      advisorIds,
      advisorFilterActive,
      returnDateFrom,
      returnDateTo,
    ],
  );

  const {
    data: response,
    isLoading,
    error,
    isFetching,
  } = useSupplierInsights(queryParams);

  const allSuppliers = useMemo(() => {
    if (!response?.data) return [];
    return response.data;
  }, [response]);

  const totalInvoices = useMemo(
    () => allSuppliers.reduce((sum, d) => sum + d.invoiceCount, 0),
    [allSuppliers],
  );

  // Pie chart: top N + "Other" bucket (by invoice count for distribution)
  const pieData = useMemo(() => {
    if (allSuppliers.length === 0) return [];
    const byInvoiceVolume = [...allSuppliers].sort(
      (a, b) => b.invoiceCount - a.invoiceCount,
    );
    const withPercent = byInvoiceVolume.map((d) => ({
      ...d,
      percent: totalInvoices > 0 ? (d.invoiceCount / totalInvoices) * 100 : 0,
    }));

    if (withPercent.length <= PIE_TOP_N) return withPercent;

    const top = withPercent.slice(0, PIE_TOP_N);
    const rest = withPercent.slice(PIE_TOP_N);
    const otherCount = rest.reduce((s, r) => s + r.invoiceCount, 0);
    const otherPercent =
      totalInvoices > 0 ? (otherCount / totalInvoices) * 100 : 0;

    return [
      ...top,
      {
        supplier: "Other",
        invoiceCount: otherCount,
        totalSales: rest
          .reduce((s, r) => s + (parseFloat(r.totalSales) || 0), 0)
          .toFixed(2),
        totalCommission: rest
          .reduce((s, r) => s + (parseFloat(r.totalCommission) || 0), 0)
          .toFixed(2),
        percent: otherPercent,
      },
    ];
  }, [allSuppliers, totalInvoices]);

  // Table: sorted + paginated over ALL suppliers
  const sortedData = useMemo(() => {
    const copy = allSuppliers.map((d) => ({
      ...d,
      percent: totalInvoices > 0 ? (d.invoiceCount / totalInvoices) * 100 : 0,
    }));
    copy.sort((a, b) => {
      if (sortField === "supplier") {
        const cmp = a.supplier.localeCompare(b.supplier);
        return sortDir === "asc" ? cmp : -cmp;
      }
      let va: number, vb: number;
      switch (sortField) {
        case "totalSales":
          va = parseFloat(a.totalSales) || 0;
          vb = parseFloat(b.totalSales) || 0;
          break;
        case "totalCommission":
          va = parseFloat(a.totalCommission) || 0;
          vb = parseFloat(b.totalCommission) || 0;
          break;
        default:
          va = a.invoiceCount;
          vb = b.invoiceCount;
      }
      return sortDir === "desc" ? vb - va : va - vb;
    });
    return copy;
  }, [allSuppliers, totalInvoices, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / PAGE_SIZE));

  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(0, totalPages - 1)));
  }, [totalPages, sortedData.length]);

  const paginatedData = useMemo(
    () => sliceForSnapshotPrint(sortedData, page, PAGE_SIZE, isPrinting),
    [sortedData, page, isPrinting],
  );

  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      } else {
        setSortField(field);
        setSortDir(field === "supplier" ? "asc" : "desc");
      }
      setPage(0);
    },
    [sortField],
  );

  if (error) {
    return (
      <SectionLoadError
        sectionName="Supplier Insights"
        error={error}
        onRetry={() => {
          queryClient.invalidateQueries({
            queryKey: ["/api/invoices/supplier-insights"],
          });
        }}
      />
    );
  }

  return (
    <div className="rounded-xl border bg-card flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Supplier Insights</h3>
          <InfoHint label="How supplier insights is calculated">
            <p>
              Supplier distribution based on paid invoices from the last 12
              months. The pie chart shows the top 5 suppliers.
            </p>
            <KpiBasisNote card="supplierInsights" />
          </InfoHint>
          <KpiBasisLabel card="supplierInsights" className="hidden sm:block" />
          {isFetching && !isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </div>
        <div className="flex items-center gap-1 print:hidden">
          {hasCompanyAccess && viewMode === "all" && (
            <AdvisorFilter
              options={advisorOptions}
              selected={advisorIds}
              onChange={(ids) => {
                setAdvisorIds(ids);
                setPage(0);
              }}
            />
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="p-4 space-y-3">
          <Skeleton className="h-[180px] w-[180px] rounded-full mx-auto" />
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </div>
      ) : allSuppliers.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 min-h-[280px]">
          <Building2 className="h-8 w-8 text-muted-foreground/20 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">
            No supplier data
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-[240px] text-center">
            Paid invoice suppliers from the last 12 months will appear here.
          </p>
        </div>
      ) : (
        <div className="flex flex-col flex-1">
          {/* Pie Chart — AR-210 (TC-13.2): summarised for screen readers; the full
              numbers are in the supplier table below. */}
          <div
            className="px-4 pt-4 pb-2 flex-1 min-h-[250px]"
            role="img"
            aria-label={`Top suppliers by invoice count: ${pieData.map((d) => `${d.supplier} ${d.percent.toFixed(0)}%`).join(", ")}`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="invoiceCount"
                  nameKey="supplier"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  label={renderPieLabel}
                  labelLine={{ strokeWidth: 1 }}
                >
                  {pieData.map((_, idx) => (
                    <Cell
                      key={idx}
                      fill={AIVIA_COLORS[idx % AIVIA_COLORS.length]}
                      stroke="hsl(var(--background))"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <RechartsTooltip content={<PieTooltipContent />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="overflow-x-auto border-t">
            <table className="w-full text-sm" role="table">
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-gray-800/50">
                  <SortableHeader
                    label="Supplier"
                    field="supplier"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    align="left"
                  />
                  <SortableHeader
                    label="Amount"
                    field="invoiceCount"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    className="hidden md:table-cell"
                  />
                  <SortableHeader
                    label="Sales"
                    field="totalSales"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Gross Commission"
                    field="totalCommission"
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row) => (
                  <tr
                    key={`${row.supplier}|${row.invoiceCount}|${row.totalSales}`}
                    className="border-b last:border-b-0 hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-2 py-2.5 text-sm font-medium break-words md:px-3 max-w-[140px]">
                      {row.supplier}
                    
                      <span className="mt-0.5 block text-xs font-normal text-muted-foreground md:hidden">
                        {row.invoiceCount} invoice{row.invoiceCount === 1 ? "" : "s"}
                      </span>
                    </td>
                    <td className="hidden px-2 py-2.5 text-sm text-center tabular-nums md:table-cell md:px-3">
                      {row.invoiceCount}
                    </td>
                    <td className="px-2 py-2.5 text-sm text-center tabular-nums md:px-3">
                      {formatCurrency(parseFloat(row.totalSales) || 0)}
                    </td>
                    <td className="px-2 py-2.5 text-sm text-center tabular-nums md:px-3">
                      {formatCurrency(parseFloat(row.totalCommission) || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isPrinting && sortedData.length > 0 && (
            <p className="hidden print:block px-3 py-2 text-xs text-gray-500 border-t">
              {sortedData.length} supplier{sortedData.length === 1 ? "" : "s"}
            </p>
          )}

          {/* Pagination */}
          {totalPages > 1 && !isPrinting && (
            <div className="flex items-center justify-end gap-2 px-3 py-2 border-t print:hidden">
              <Button
                variant="ghost"
                size="sm"
                className="h-11 w-11 p-0 md:h-7 md:w-7"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-11 w-11 p-0 md:h-7 md:w-7"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
